import { useServices } from '@/app/providers';
import { Button } from '@/core/ui/components/Button';
import { Input } from '@/core/ui/components/Input';
import { useI18n } from '@/shared/i18n';
import type { TenantSelection } from '@yawi/types';
import { Building2, Eye, EyeOff, X } from 'lucide-react';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';

interface TenantSwitchModalProps {
  tenant: TenantSelection;
  onClose: () => void;
}

/**
 * Cada residencial es una cuenta distinta (el alta crea un MasterUser por
 * tenant, con su propia contraseña), así que cambiar de residencial es un login
 * dirigido y no un cambio de contexto: hay que pedir la contraseña del destino.
 */
export const TenantSwitchModal: React.FC<TenantSwitchModalProps> = ({ tenant, onClose }) => {
  const { t } = useI18n();
  const { switchTenant } = useServices();

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;

    setError('');
    setLoading(true);
    try {
      // En caso de éxito recarga la app entera, así que no hace falta cerrar.
      await switchTenant(tenant, password);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('tenantSwitch.error'));
      setLoading(false);
    }
  };

  // Portal a body: el sidebar tiene `transform`, que crea bloque contenedor
  // para position:fixed y encerraba el modal dentro de sus 256px.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="flex justify-between items-center p-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Building2 size={20} className="text-primary-600" />
            </div>
            <h3 className="font-bold text-slate-800 text-lg">{t('tenantSwitch.title')}</h3>
          </div>
          <button onClick={onClose} type="button">
            <X size={20} className="text-slate-400 hover:text-slate-600 transition-colors" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-sm text-slate-500">
            {t('tenantSwitch.description', { tenant: tenant.tenantName })}
          </p>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="relative flex items-center">
            <Input
              label={t('tenantSwitch.passwordLabel')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute inset-y-0 right-3 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors mt-6"
              aria-label={showPassword ? t('common.hide') : t('common.show')}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" className="flex-1" isLoading={loading}>
              {t('tenantSwitch.confirm')}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};
